import { useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ExpenseCategoryListSchema,
  ExpenseClaimDetailSchema,
  ExpenseClaimSchema,
  PaymentDetailSchema,
  type ClientDto,
  type ExpenseCategoryDto,
  type ExpenseClaimDetailDto,
  type ProjectDto,
} from '@taqon/contracts';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { getData, getPage, newIdempotencyKey, patchJson, postJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { ExportButton } from '../../shared/export/ExportButton';
import { exportHref } from '../../shared/export/export-href';
import { hasCapability, useSession } from '../../shared/session/session';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { PersonName } from '../../shared/ui/person';
import { useUrlList } from '../../shared/ui/url-list';
import { ApprovalPanel, workflowHistoryKey } from '../../shared/workflow/ApprovalPanel';
import { ActiveClientPicker, ActiveProjectPicker } from '../crm/common';
import { refreshAfterPosting } from '../finance/cache';
import { DimensionRef, Facts, JournalRef, RecordFailure, RecordLink } from '../finance/detail';
import { businessToday } from '../finance/line-editor';
import { MasterSelect, useActiveMasters } from '../finance/lookups';
import { isAmount, sum, toFils } from '../finance/money';
import { RefusalState } from '../finance/refusal';
import { SearchFilterBar } from '../../shared/ui/filters';
import { useActionFeedback } from '../happiness/ActionFeedback';

const CLAIMS = '/api/v1/finance/expense-claims';
const CLAIM = 'finance.expense_claim';
const EDITABLE = ['draft', 'returned', 'rejected'];

/** Stable React keys for the editor's lines; never shown or sent. */
let lineKeys = 0;
const nextLineKey = () => ++lineKeys;

/**
 * Employee expense claims (FRD-FIN-082 to FIN-090): an employee's own claims — or, for Finance and
 * approvers, those they may review — with the status through submission, approval, posting as a
 * payable and payment. The list is filtered on the server with the filters in the URL.
 */
export function ExpenseClaimsPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, `${CLAIM}:create`);
  const list = useUrlList({ pageSize: 50 });
  const params = list.apiParams(
    { search: 'search', status: 'status', from: 'month_from', to: 'month_to' },
    '-claim_month',
  );
  const claims = useQuery({
    queryKey: ['expense-claims', params.toString()],
    queryFn: () => getPage(`${CLAIMS}?${params.toString()}`, ExpenseClaimSchema),
  });
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Expense claims"
        description="Monthly business expense claims, their approval, posting and payment."
        actions={
          <>
            <ExportButton recordType={CLAIM} href={exportHref(CLAIMS, params)} />
            {canCreate && (
              <Button
                component={RouterLink}
                to="/expense-claims/new"
                variant="contained"
                startIcon={<AddIcon />}
              >
                New claim
              </Button>
            )}
          </>
        }
      />
      <Box role="search" aria-label="Filter the claims">
        <SearchFilterBar
          label="Search number or description"
          value={list.get('search')}
          onChange={(value) => {
            list.set('search', value);
          }}
        >
          <TextField
            select
            size="small"
            label="Status"
            value={list.get('status')}
            sx={{ minWidth: 160 }}
            onChange={(e) => {
              list.set('status', e.target.value);
            }}
          >
            <MenuItem value="">Any</MenuItem>
            {['draft', 'submitted', 'returned', 'rejected', 'approved', 'payable', 'paid'].map(
              (status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ),
            )}
          </TextField>
          <TextField
            size="small"
            type="date"
            label="Month from"
            value={list.get('from')}
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(e) => {
              list.set('from', e.target.value);
            }}
          />
          <TextField
            size="small"
            type="date"
            label="Month to"
            value={list.get('to')}
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(e) => {
              list.set('to', e.target.value);
            }}
          />
        </SearchFilterBar>
      </Box>
      <Section>
        {claims.isPending && <LoadingState label="Loading claims" />}
        {claims.isError && <ErrorState message={claims.error.message} />}
        {claims.data?.items.length === 0 && <EmptyState title="No claims found" />}
        {claims.data !== undefined && claims.data.items.length > 0 && (
          <>
            <Table size="small" aria-label="Expense claims">
              <TableHead>
                <TableRow>
                  <TableCell>Claim</TableCell>
                  <TableCell>Employee</TableCell>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Outstanding</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {claims.data.items.map((claim) => (
                  <TableRow key={claim.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <RecordLink to={`/expense-claims/${claim.id}`}>{claim.documentNo}</RecordLink>
                    </TableCell>
                    <TableCell>
                      {claim.employeeLabel == null ? (
                        '—'
                      ) : (
                        <PersonName name={claim.employeeLabel} employeeId={claim.employeeId} />
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{claim.claimMonth}</TableCell>
                    <TableCell align="right">{claim.totalAmount}</TableCell>
                    <TableCell align="right">{claim.outstandingAmount}</TableCell>
                    <TableCell>
                      <StatusChip status={claim.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={claims.data.total}
              page={list.page}
              rowsPerPage={list.pageSize}
              rowsPerPageOptions={[25, 50, 100]}
              onPageChange={(_, next) => {
                list.setPage(next);
              }}
              onRowsPerPageChange={(e) => {
                list.setPageSize(Number.parseInt(e.target.value, 10));
              }}
            />
          </>
        )}
      </Section>
    </Stack>
  );
}

interface LineDraft {
  key: number;
  expenseDate: string;
  description: string;
  amount: string;
  categoryId: string;
  costCenterId: string;
  project: ProjectDto | null;
  projectId: string | null;
  client: ClientDto | null;
  clientId: string | null;
}

/** The problems that keep a claim from being saved, in words (FRD-FIN-083 to FIN-085). */
export function claimProblems(
  month: string,
  lines: readonly LineDraft[],
  categories: readonly ExpenseCategoryDto[],
): string[] {
  const problems: string[] = [];
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) problems.push('Choose the claim month.');
  if (lines.length === 0) problems.push('Add at least one line.');
  lines.forEach((line, index) => {
    const n = `Line ${String(index + 1)}`;
    const category = categories.find((c) => c.id === line.categoryId);
    if (category === undefined) problems.push(`${n}: choose a category.`);
    if (line.description.trim() === '') problems.push(`${n}: describe the expense.`);
    if (!isAmount(line.amount) || toFils(line.amount) <= 0n) {
      problems.push(`${n}: enter an amount above zero.`);
    }
    if (line.expenseDate === '' || !line.expenseDate.startsWith(month)) {
      problems.push(`${n}: the date must be in ${month === '' ? 'the claim month' : month}.`);
    }
    if (category?.requires.costCenter === true && line.costCenterId === '') {
      problems.push(`${n}: account ${category.accountCode ?? ''} requires a cost centre.`);
    }
    if (category?.requires.project === true && line.projectId === null) {
      problems.push(`${n}: account ${category.accountCode ?? ''} requires a project.`);
    }
    if (category?.requires.client === true && line.clientId === null) {
      problems.push(`${n}: account ${category.accountCode ?? ''} requires a client.`);
    }
  });
  return problems;
}

/** A new claim, or a change to a draft, returned or rejected one (FRD-FIN-082, FIN-089). */
export function ExpenseClaimEditorPage() {
  const { id } = useParams();
  const existing = useQuery({
    queryKey: ['expense-claim', id],
    enabled: id !== undefined,
    queryFn: () => getData(`${CLAIMS}/${id ?? ''}`, ExpenseClaimDetailSchema),
  });
  if (id !== undefined && existing.isPending) return <LoadingState label="Loading the claim" />;
  if (existing.isError) {
    return (
      <RecordFailure
        title="Expense claim"
        noun="claim"
        error={existing.error}
        back={
          <Button component={RouterLink} to="/expense-claims" startIcon={<ArrowBackIcon />}>
            All claims
          </Button>
        }
      />
    );
  }
  return <ClaimEditor key={id ?? 'new'} existing={existing.data} />;
}

function ClaimEditor({ existing }: { existing: ExpenseClaimDetailDto | undefined }) {
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canSeeCostCentres = hasCapability(session.data, 'organization.cost_center:view');
  const costCentres = useActiveMasters('/api/v1/organization/cost-centers', canSeeCostCentres);
  const categories = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => getData('/api/v1/finance/expense-categories', ExpenseCategoryListSchema),
  });
  const active = (categories.data ?? []).filter(
    (c) => c.status === 'active' || existing?.lines.some((l) => l.expenseCategoryId === c.id),
  );
  const blank = (month: string): LineDraft => ({
    key: nextLineKey(),
    expenseDate: month === '' ? '' : `${month}-01`,
    description: '',
    amount: '',
    categoryId: '',
    costCenterId: '',
    project: null,
    projectId: null,
    client: null,
    clientId: null,
  });
  const [month, setMonth] = useState(
    existing?.claim.claimMonth ?? businessToday(session.data?.businessTimezone).slice(0, 7),
  );
  const [description, setDescription] = useState(existing?.claim.description ?? '');
  const [lines, setLines] = useState<LineDraft[]>(() =>
    existing === undefined
      ? [blank(month)]
      : existing.lines.map((line) => ({
          key: nextLineKey(),
          expenseDate: line.expenseDate,
          description: line.description,
          amount: line.amount,
          categoryId: line.expenseCategoryId,
          costCenterId: line.costCenterId ?? '',
          project: null,
          projectId: line.projectId,
          client: null,
          clientId: line.clientId,
        })),
  );
  const [touched, setTouched] = useState(false);
  const idempotencyKey = useRef(newIdempotencyKey());
  const update = (key: number, change: Partial<LineDraft>) => {
    setLines((current) =>
      current.map((line) => (line.key === key ? { ...line, ...change } : line)),
    );
  };
  const problems = claimProblems(month, lines, categories.data ?? []);
  const body = {
    claimMonth: month,
    ...(description.trim() === '' ? {} : { description: description.trim() }),
    lines: lines.map((line) => ({
      expenseDate: line.expenseDate,
      description: line.description.trim(),
      amount: line.amount.trim(),
      expenseCategoryId: line.categoryId,
      ...(line.costCenterId === '' ? {} : { costCenterId: line.costCenterId }),
      ...(line.projectId === null ? {} : { projectId: line.projectId }),
      ...(line.clientId === null ? {} : { clientId: line.clientId }),
    })),
  };
  const save = useMutation({
    mutationFn: () =>
      existing === undefined
        ? postJson(CLAIMS, body, ExpenseClaimDetailSchema, session.data?.csrfToken ?? '', {
            idempotencyKey: idempotencyKey.current,
          })
        : patchJson(
            `${CLAIMS}/${existing.claim.id}`,
            body,
            ExpenseClaimDetailSchema,
            session.data?.csrfToken ?? '',
            existing.claim.version,
          ),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['expense-claims'] });
      await queryClient.invalidateQueries({ queryKey: ['expense-claim', saved.claim.id] });
      void navigate(`/expense-claims/${saved.claim.id}`);
    },
  });
  return (
    <Stack spacing={3}>
      <PageHeader
        title={existing === undefined ? 'New expense claim' : `Change ${existing.claim.documentNo}`}
        description="One claim per month; attach receipts on the claim's page once it is saved."
        actions={
          <Button
            component={RouterLink}
            to={existing === undefined ? '/expense-claims' : `/expense-claims/${existing.claim.id}`}
            startIcon={<ArrowBackIcon />}
          >
            Cancel
          </Button>
        }
      />
      <Section title="Claim">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            size="small"
            type="month"
            label="Claim month"
            required
            value={month}
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={(e) => {
              setMonth(e.target.value);
            }}
          />
          <TextField
            size="small"
            label="Description"
            value={description}
            sx={{ flex: 1 }}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
          />
        </Stack>
      </Section>
      <Section title="Lines">
        {categories.isError && <ErrorState message={categories.error.message} />}
        {categories.data?.length === 0 && (
          <Alert severity="info" variant="outlined">
            No expense categories are configured yet. Finance maps each category to a COA account
            before claims can be raised.
          </Alert>
        )}
        <Stack spacing={2}>
          {lines.map((line, index) => {
            const category = active.find((c) => c.id === line.categoryId);
            return (
              <Box
                key={line.key}
                component="fieldset"
                aria-label={`Line ${String(index + 1)}`}
                sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}
              >
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                      select
                      size="small"
                      label="Category"
                      required
                      value={line.categoryId}
                      sx={{ minWidth: 240 }}
                      helperText={
                        category === undefined
                          ? undefined
                          : `Posts to ${category.accountCode ?? ''} ${category.accountName ?? ''}`
                      }
                      onChange={(e) => {
                        update(line.key, { categoryId: e.target.value });
                      }}
                    >
                      {active.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      type="date"
                      label="Date"
                      required
                      value={line.expenseDate}
                      slotProps={{ inputLabel: { shrink: true } }}
                      onChange={(e) => {
                        update(line.key, { expenseDate: e.target.value });
                      }}
                    />
                    <TextField
                      size="small"
                      label="Description"
                      required
                      value={line.description}
                      sx={{ flex: 1 }}
                      onChange={(e) => {
                        update(line.key, { description: e.target.value });
                      }}
                    />
                    <TextField
                      size="small"
                      label="Amount (AED)"
                      required
                      value={line.amount}
                      sx={{ width: 140 }}
                      onChange={(e) => {
                        update(line.key, { amount: e.target.value });
                      }}
                    />
                    <IconButton
                      aria-label={`Remove line ${String(index + 1)}`}
                      disabled={lines.length === 1}
                      onClick={() => {
                        setLines((current) => current.filter((l) => l.key !== line.key));
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <MasterSelect
                      label="Cost centre"
                      value={line.costCenterId}
                      required={category?.requires.costCenter === true}
                      disabled={!canSeeCostCentres}
                      options={costCentres.data?.items ?? []}
                      onChange={(value) => {
                        update(line.key, { costCenterId: value });
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <ActiveProjectPicker
                        label="Project"
                        value={line.project}
                        required={category?.requires.project === true}
                        helperText={
                          line.project === null && line.projectId !== null
                            ? `Project #${line.projectId}`
                            : undefined
                        }
                        onChange={(project) => {
                          update(line.key, { project, projectId: project?.id ?? null });
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <ActiveClientPicker
                        label="Client"
                        value={line.client}
                        required={category?.requires.client === true}
                        helperText={
                          line.client === null && line.clientId !== null
                            ? `Client #${line.clientId}`
                            : undefined
                        }
                        onChange={(client) => {
                          update(line.key, { client, clientId: client?.id ?? null });
                        }}
                      />
                    </Box>
                  </Stack>
                </Stack>
              </Box>
            );
          })}
          <Box>
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                setLines((current) => [...current, blank(month)]);
              }}
            >
              Add line
            </Button>
          </Box>
          <Box sx={{ textAlign: 'right', fontWeight: 700 }}>
            Total AED {sum(lines.map((line) => (isAmount(line.amount) ? line.amount : '0')))}
          </Box>
        </Stack>
      </Section>
      {touched && problems.length > 0 && (
        <Alert severity="warning" variant="outlined">
          {problems.map((p) => (
            <div key={p}>{p}</div>
          ))}
        </Alert>
      )}
      {save.isError && <RefusalState error={save.error} />}
      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          disabled={save.isPending}
          onClick={() => {
            setTouched(true);
            if (problems.length === 0) save.mutate();
          }}
        >
          {save.isPending ? 'Saving…' : 'Save claim'}
        </Button>
      </Stack>
    </Stack>
  );
}

/**
 * One claim (`/expense-claims/:id`): its lines, the approval route, receipts, history and the
 * actions its status allows — submit (the employee), post as a payable and pay (Finance).
 */
export function ExpenseClaimDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const feedback = useActionFeedback();
  const can = (capability: string) => hasCapability(session.data, capability);
  const detail = useQuery({
    queryKey: ['expense-claim', id],
    queryFn: () => getData(`${CLAIMS}/${id}`, ExpenseClaimDetailSchema),
  });
  const [dialog, setDialog] = useState<'submit' | 'post' | 'pay' | null>(null);
  const [postingDate, setPostingDate] = useState(businessToday(session.data?.businessTimezone));
  const [registerId, setRegisterId] = useState('');
  const costCentres = useActiveMasters(
    '/api/v1/organization/cost-centers',
    can('organization.cost_center:view'),
  );
  const registers = useActiveMasters(
    '/api/v1/finance/bank-cash-accounts',
    dialog === 'pay' && can('finance.bank_cash_account:view'),
  );
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['expense-claim', id] });
    await queryClient.invalidateQueries({ queryKey: ['expense-claims'] });
    await queryClient.invalidateQueries({ queryKey: workflowHistoryKey(CLAIM, id) });
  };
  const act = useMutation({
    mutationFn: async (kind: 'submit' | 'post') => {
      const current = detail.data?.claim;
      if (current === undefined) throw new Error('The claim is not loaded');
      return postJson(
        `${CLAIMS}/${id}/${kind}`,
        kind === 'post' ? { postingDate } : {},
        ExpenseClaimDetailSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: current.version },
      );
    },
    onSuccess: async (_saved, kind) => {
      setDialog(null);
      // Just submitted: offer to rate how it went (FRD-CORE-062), once a day.
      if (kind === 'submit') feedback.offer('expense_claim');
      await refresh();
      await refreshAfterPosting(queryClient);
    },
  });
  const pay = useMutation({
    mutationFn: () => {
      const current = detail.data?.claim;
      if (current === undefined) throw new Error('The claim is not loaded');
      return postJson(
        '/api/v1/finance/employee-payments',
        {
          employeeId: current.employeeId,
          paymentDate: businessToday(session.data?.businessTimezone),
          bankCashAccountId: registerId,
          claims: [{ expenseClaimId: current.id, allocatedAmount: current.outstandingAmount }],
        },
        PaymentDetailSchema,
        session.data?.csrfToken ?? '',
        { idempotencyKey: newIdempotencyKey() },
      );
    },
    onSuccess: async (payment) => {
      await refresh();
      void navigate(`/payments/${payment.payment.id}`);
    },
  });
  const back = (
    <Button component={RouterLink} to="/expense-claims" startIcon={<ArrowBackIcon />}>
      All claims
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the claim" />;
  if (detail.isError) {
    return <RecordFailure title="Expense claim" noun="claim" error={detail.error} back={back} />;
  }
  const { claim, lines } = detail.data;
  const editable = EDITABLE.includes(claim.status);
  // The employee (own-records grant) or someone who may change claims; the API decides which.
  const mayEdit = editable && (can(`${CLAIM}:create`) || can(`${CLAIM}:change`));
  const costCentreName = (ccId: string) => {
    const cc = costCentres.data?.items.find((item) => item.id === ccId);
    return cc === undefined ? `Cost centre #${ccId}` : `${cc.code} — ${cc.name}`;
  };
  const outstanding = toFils(claim.outstandingAmount) > 0n;
  return (
    <Stack spacing={3}>
      <PageHeader
        title={`Expense claim ${claim.documentNo}`}
        description={`${claim.employeeLabel ?? 'Employee'} — ${claim.claimMonth}`}
        actions={
          <>
            {back}
            {mayEdit && (
              <Button component={RouterLink} to={`/expense-claims/${claim.id}/edit`}>
                Edit
              </Button>
            )}
            {mayEdit && can(`${CLAIM}:create`) && (
              <Button
                variant="contained"
                onClick={() => {
                  setDialog('submit');
                }}
              >
                {claim.status === 'draft' ? 'Submit for approval' : 'Resubmit'}
              </Button>
            )}
            {claim.status === 'approved' && can(`${CLAIM}:post`) && (
              <Button
                variant="contained"
                onClick={() => {
                  setDialog('post');
                }}
              >
                Post payable
              </Button>
            )}
            {claim.status === 'payable' && outstanding && can('finance.payment:create') && (
              <Button
                variant="contained"
                onClick={() => {
                  setDialog('pay');
                }}
              >
                Pay employee
              </Button>
            )}
          </>
        }
      />
      {claim.status === 'approved' && (
        <Alert severity="info" variant="outlined">
          Approved. Finance posts it as a payable to the employee; that needs the employee
          claim-payable account, which Finance must supply and map (CLR-02) — until then posting is
          refused and nothing is assumed.
        </Alert>
      )}
      <Section title="Claim">
        <Facts
          label="Claim details"
          facts={[
            ['Status', <StatusChip key="s" status={claim.status} />],
            [
              'Employee',
              claim.employeeLabel == null ? (
                '—'
              ) : (
                <PersonName key="e" name={claim.employeeLabel} employeeId={claim.employeeId} />
              ),
            ],
            ['Month', claim.claimMonth],
            ['Description', claim.description ?? '—'],
            ['Total', claim.totalAmount],
            ['Payable', claim.payableTotal],
            ['Paid', claim.paidAmount],
            ['Outstanding', claim.outstandingAmount],
            ['Journal', <JournalRef key="j" journal={detail.data.journal} />],
          ]}
        />
      </Section>
      <Section title="Lines">
        <Table size="small" aria-label="Claim lines">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Account</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Dimensions</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line) => (
              <TableRow key={line.id}>
                <TableCell>{line.lineNo}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{line.expenseDate}</TableCell>
                <TableCell>{line.categoryName ?? '—'}</TableCell>
                <TableCell>{line.accountCode ?? '—'}</TableCell>
                <TableCell>{line.description}</TableCell>
                <TableCell>
                  <Stack spacing={0.5}>
                    {line.costCenterId !== null && <span>{costCentreName(line.costCenterId)}</span>}
                    {line.projectId !== null && <DimensionRef kind="project" id={line.projectId} />}
                    {line.clientId !== null && <DimensionRef kind="client" id={line.clientId} />}
                  </Stack>
                </TableCell>
                <TableCell align="right">{line.amount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
      {detail.data.payments.length > 0 && (
        <Section title="Payments">
          <Table size="small" aria-label="Claim payments">
            <TableHead>
              <TableRow>
                <TableCell>Payment</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Allocated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {detail.data.payments.map((payment) => (
                <TableRow key={payment.paymentId}>
                  <TableCell>
                    <RecordLink to={`/payments/${payment.paymentId}`}>
                      {payment.documentNo}
                    </RecordLink>
                  </TableCell>
                  <TableCell>{payment.paymentDate}</TableCell>
                  <TableCell>
                    <StatusChip status={payment.status} />
                  </TableCell>
                  <TableCell align="right">{payment.allocatedAmount}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      )}
      <ApprovalPanel recordType={CLAIM} recordId={claim.id} onDecided={() => void refresh()} />
      <DocumentsPanel
        recordType={CLAIM}
        recordId={claim.id}
        canAttach={mayEdit}
        title="Receipts and supporting documents"
      />
      <RecordHistory recordType={CLAIM} recordId={claim.id} />
      {feedback.dialog}
      {(dialog === 'submit' || dialog === 'post') && (
        <ConfirmDialog
          open
          title={dialog === 'submit' ? `Submit ${claim.documentNo}?` : `Post ${claim.documentNo}?`}
          message={
            dialog === 'submit'
              ? 'The claim goes to its approvers and cannot be changed unless it is returned.'
              : `Posts AED ${claim.totalAmount} as a payable to the employee.`
          }
          confirmLabel={dialog === 'submit' ? 'Submit' : 'Post'}
          busy={act.isPending}
          error={act.isError ? act.error.message : null}
          onCancel={() => {
            setDialog(null);
          }}
          onConfirm={() => {
            act.mutate(dialog);
          }}
        >
          {dialog === 'post' && (
            <TextField
              size="small"
              type="date"
              label="Posting date"
              value={postingDate}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => {
                setPostingDate(e.target.value);
              }}
            />
          )}
        </ConfirmDialog>
      )}
      {dialog === 'pay' && (
        <FormDialog
          open
          title={`Pay ${claim.documentNo}`}
          submitLabel="Create payment"
          canSubmit={registerId !== ''}
          submitting={pay.isPending}
          error={pay.isError ? pay.error.message : null}
          onClose={() => {
            setDialog(null);
          }}
          onSubmit={() => {
            pay.mutate();
          }}
        >
          <MasterSelect
            label="Pay from"
            value={registerId}
            required
            options={registers.data?.items ?? []}
            onChange={setRegisterId}
          />
          <Box>
            Pays the outstanding AED {claim.outstandingAmount}. The payment is created as a draft;
            post it on its own page.
          </Box>
        </FormDialog>
      )}
    </Stack>
  );
}
